<script lang="ts" setup>
import { cn } from '@/lib/utils'
import { CalendarRoot, type CalendarRootEmits, type CalendarRootProps, useForwardPropsEmits } from 'radix-vue'
import { computed, ref, type HTMLAttributes } from 'vue'
import { CalendarCell, CalendarCellTrigger, CalendarGrid, CalendarGridBody, CalendarGridHead, CalendarGridRow, CalendarHeadCell, CalendarHeader, CalendarHeading, CalendarNextButton, CalendarPrevButton } from '.'
import TimePicker from '../time/TimePicker.vue' // Importando o TimePicker

interface ExtendedCalendarRootEmits extends CalendarRootEmits {
  (e: 'update:modelValue', value: Date): void
}

const props = withDefaults(defineProps<CalendarRootProps>(), {})

const emits = defineEmits()

const delegatedProps = computed(() => {
  const { ...delegated } = props
  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)

const selectedTime = ref('00:00')
const selectedDate = ref<Date | null>(null)

const updateDateTime = () => {
  if (selectedDate.value) {
    const [hours, minutes] = selectedTime.value.split(':').map(Number)
    const newDate = new Date(selectedDate.value)
    newDate.setHours(hours, minutes)
    emits('update:modelValue', newDate)
  }
}

const onDateSelect = (date: Date) => {
  selectedDate.value = date
  updateDateTime()
}

const onTimeChange = (time: string) => {
  selectedTime.value = time
  updateDateTime()
}
</script>

<template>
  <CalendarRoot
    v-slot="{ grid, weekDays }"
    :class="cn('p-3')"
    v-bind="forwarded"
    :model-value="selectedDate"
    @update:model-value="onDateSelect"
  >
    <CalendarHeader>
      <CalendarPrevButton />
      <CalendarHeading />
      <CalendarNextButton />
    </CalendarHeader>

    <div class="flex flex-col gap-y-4 mt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <CalendarGrid v-for="month in grid" :key="month.value.toString()">
        <CalendarGridHead>
          <CalendarGridRow>
            <CalendarHeadCell
              v-for="day in weekDays" :key="day"
            >
              {{ day }}
            </CalendarHeadCell>
          </CalendarGridRow>
        </CalendarGridHead>
        <CalendarGridBody>
          <CalendarGridRow v-for="(weekDates, index) in month.rows" :key="`weekDate-${index}`" class="mt-2 w-full">
            <CalendarCell
              v-for="weekDate in weekDates"
              :key="weekDate.toString()"
              :date="weekDate"
            >
              <CalendarCellTrigger
                :day="weekDate"
                :month="month.value"
              />
            </CalendarCell>
          </CalendarGridRow>
        </CalendarGridBody>
      </CalendarGrid>
    </div>

    <div class="mt-4 flex justify-center">
      <TimePicker v-model="selectedTime" @change="onTimeChange" />
    </div>
  </CalendarRoot>
</template>